<script setup lang="ts">
import { toast } from '~/utils/sonner'
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-vue-next'

const route = useRoute()
const visitorSession = useVisitorSession()
const $i18n = useI18n()
const $t = $i18n.t

const uuid = computed(() => {
  const v = route.query.uuid
  return Array.isArray(v) ? v[0] : (v ?? '')
})

const rawCode = ref('')
const submitting = ref(false)
const confirmed = ref(false)
const codeInputRef = ref<HTMLInputElement | null>(null)

const isUUIDValid = computed(() => {
  // 8-4-4-4-12 hex with hyphens.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid.value)
})

// What the user typed, normalized to the eight alphanumeric chars the server
// expects. Single source of truth — derived from `rawCode` on every keystroke.
const normalizedCode = computed(() => {
  return rawCode.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 8)
})

// What the user sees in the input: ABCD-1234 once they've typed 5+ chars.
// Reformatting on every keystroke lets the dash appear/disappear automatically
// as the user backspaces.
const displayCode = computed({
  get() {
    const n = normalizedCode.value
    return n.length > 4 ? `${n.slice(0, 4)}-${n.slice(4)}` : n
  },
  set(v: string) {
    rawCode.value = v
  },
})

const codeChars = computed(() => {
  return Array.from({ length: 8 }, (_, index) => normalizedCode.value[index] ?? '')
})

const canSubmit = computed(() => {
  return isUUIDValid.value && normalizedCode.value.length === 8 && !submitting.value && !confirmed.value
})

const loginRedirect = computed(() => {
  return `/login?redirect=${encodeURIComponent(route.fullPath)}`
})

function focusCodeInput() {
  codeInputRef.value?.focus()
}

async function onConfirm() {
  if (!canSubmit.value) return
  submitting.value = true
  try {
    await useAPI().post(`device_codes/${uuid.value}/confirm`, {
      body: { code: normalizedCode.value },
    })
    confirmed.value = true
  } catch (e: any) {
    const realErr = e.data?.data || e.data
    const errKey = realErr?.message || e.message
    const isWrong = errKey === 'notValid'
    const isExpired = errKey === 'notFound'
    toast({
      title: isWrong
        ? $t('auth_tv.wrong_code_title')
        : isExpired
          ? $t('auth_tv.expired_title')
          : $t('auth_tv.error_title'),
      description: isWrong
        ? $t('auth_tv.wrong_code_message')
        : isExpired
          ? $t('auth_tv.expired_message')
          : $t('auth_tv.error_message'),
      variant: 'destructive',
    })
  } finally {
    submitting.value = false
  }
}
</script>

<template lang="pug">
.auth-tv-page-outer
  .auth-tv-page-wrap
    .auth-card.rounded-2xl.p-6(class="sm:p-8")
      .text-center.mb-8
        .mb-4.flex.items-center.justify-center.gap-3
          .flex.h-12.w-12.items-center.justify-center.rounded-full(class="bg-primary/10")
            img.h-7.w-7.rounded-lg(src="/img/logo-color.svg" alt="PandaFan")
          .text-lg.font-semibold.text-muted-foreground +
          .flex.h-12.w-12.items-center.justify-center.rounded-full(class="bg-primary/10")
            Icon(name="simple-icons:appletv" class="h-6 w-6 text-primary")
        h1.text-xl.font-bold {{ $t('auth_tv.title') }}
        p.mt-2.text-sm.text-muted-foreground.text-balance {{ $t('auth_tv.subtitle') }}

      template(v-if="!isUUIDValid")
        .flex.items-start.gap-3.rounded-lg.border.border-destructive(class="bg-destructive/5 p-4")
          AlertTriangle.h-5.w-5.shrink-0.text-destructive(class="mt-0.5")
          p.text-sm.text-destructive {{ $t('auth_tv.invalid_link') }}

      template(v-else-if="!visitorSession.loggedIn")
        .space-y-4.text-center
          p.text-sm.text-muted-foreground.text-balance {{ $t('auth_tv.must_login') }}
          NuxtLink.block(:to="loginRedirect")
            button.cta-button(type="button")
              .cta-shadow
              .cta-front {{ $t('auth_tv.sign_in_button') }}

      template(v-else-if="confirmed")
        .flex.flex-col.items-center.text-center.gap-3.rounded-lg(class="bg-emerald-500/10 p-6 text-emerald-700 dark:text-emerald-300")
          CheckCircle2.h-10.w-10.text-emerald-500
          p.font-medium.text-base {{ $t('auth_tv.confirmed_title') }}
          p.text-sm(class="text-emerald-700/80 dark:text-emerald-300/80 text-balance") {{ $t('auth_tv.confirmed_message') }}

      template(v-else)
        form(@submit.prevent.stop="onConfirm")
          .auth-tv-code-entry(@click="focusCodeInput")
            input.auth-tv-code-native(
              id="device-code"
              ref="codeInputRef"
              v-model="displayCode"
              :aria-label="$t('auth_tv.code_label')"
              autocomplete="off"
              autocapitalize="characters"
              autocorrect="off"
              spellcheck="false"
              inputmode="text"
              maxlength="9"
            )
            .auth-tv-code-boxes(aria-hidden="true")
              template(v-for="(char, index) in codeChars" :key="index")
                .auth-tv-code-box(:class="{ 'is-filled': Boolean(char), 'is-active': normalizedCode.length === index }") {{ char }}
                .auth-tv-code-divider(v-if="index === 3") -

          .cta-button-wrap.mt-7
            button.cta-button(type="submit" :disabled="!canSubmit")
              .cta-shadow
              .cta-front
                Loader2.mr-2.h-4.w-4.animate-spin(v-if="submitting")
                span {{ submitting ? $t('auth_tv.confirming') : $t('auth_tv.confirm_button') }}

          .mt-6.text-center.text-xs.text-muted-foreground
            | {{ $t('auth_tv.signed_in_as') }} #[span.text-foreground.font-medium {{ visitorSession.email }}]
            | &nbsp;·&nbsp;
            NuxtLink.text-primary(class="hover:underline" :to="loginRedirect") {{ $t('auth_tv.switch_account') }}
</template>

<style lang="postcss">

.auth-tv-page-outer {
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-tv-page-wrap {
  position: relative;
  z-index: 1;
  padding: 20px;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  transform: translateY(clamp(-36px, -4vh, -18px));
}

.auth-tv-page-wrap .auth-card {
  background: color-mix(in oklch, var(--card) 80%, transparent);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  box-shadow: 0 20px 40px -12px oklch(0 0 0 / 0.15);
}

.dark .auth-tv-page-wrap .auth-card {
  background: oklch(0.15 0.005 260 / 0.9);
  border: 1px solid oklch(1 0 0 / 0.08);
  box-shadow:
    inset 0 1px 0 oklch(1 0 0 / 0.05),
    0 24px 48px -20px oklch(0 0 0 / 0.55),
    0 40px 90px -30px oklch(0 0 0 / 0.72);
}

.auth-tv-page-wrap .auth-tv-code-entry {
  position: relative;
  cursor: text;
}

.auth-tv-page-wrap .auth-tv-code-native {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  cursor: text;
}

.auth-tv-page-wrap .auth-tv-code-boxes {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.auth-tv-page-wrap .auth-tv-code-box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  height: 3.25rem;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  background: oklch(0.5 0 0 / 0.05);
  color: var(--foreground);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: clamp(1.1rem, 4.5vw, 1.45rem);
  font-weight: 700;
  line-height: 1;
  transition: all 0.2s ease;
}

.auth-tv-page-wrap .auth-tv-code-box.is-filled {
  border-color: oklch(0.648 0.2 131.684 / 0.45);
}

.auth-tv-page-wrap .auth-tv-code-native:focus + .auth-tv-code-boxes .auth-tv-code-box.is-active {
  border-color: oklch(0.648 0.2 131.684);
  box-shadow: 0 0 0 2px oklch(0.648 0.2 131.684 / 0.2);
}

.auth-tv-page-wrap .auth-tv-code-divider {
  flex: 0 0 auto;
  color: var(--muted-foreground);
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1;
  opacity: 0.75;
}

.auth-tv-page-wrap .cta-button-wrap {
  padding-bottom: 6px;
  padding-right: 6px;
}

.auth-tv-page-wrap .cta-button {
  position: relative;
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.auth-tv-page-wrap .cta-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.auth-tv-page-wrap .cta-button .cta-shadow {
  position: absolute;
  inset: 0;
  background-color: oklch(0.648 0.2 131.684);
  border-radius: 0.75rem;
  transform: translate(4px, 4px);
  transition: transform 0.15s ease;
  z-index: 0;
}

.auth-tv-page-wrap .cta-button:hover:not(:disabled) .cta-shadow {
  transform: translate(6px, 6px);
}

.auth-tv-page-wrap .cta-button:active:not(:disabled) .cta-shadow {
  transform: translate(2px, 2px);
}

.auth-tv-page-wrap .cta-button .cta-front {
  position: relative;
  background-color: var(--foreground);
  color: var(--background);
  border: 2px solid var(--foreground);
  border-radius: 0.75rem;
  padding: 0.8rem 1.5rem;
  font-weight: 700;
  font-size: 0.95rem;
  z-index: 1;
  transition: transform 0.15s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
}

.auth-tv-page-wrap .cta-button:hover:not(:disabled) .cta-front {
  transform: translate(-1px, -1px);
}

.auth-tv-page-wrap .cta-button:active:not(:disabled) .cta-front {
  transform: translate(2px, 2px);
}
</style>
